import { readEnvSecondsAsMs } from "../utils/envDuration";

// Session timeline relative to the access token expiry (T):
//   T - DECISION_LEAD_MS -> decision point: a user active within the last
//                           IDLE_THRESHOLD_MS is renewed silently; an idle
//                           user (or a failed silent renewal) gets the
//                           warning popup with a 1 minute countdown
//   T - LOGOUT_BUFFER_MS -> countdown ends, forced logout
// The buffer leaves time for in-flight actions (appointment accept/decline,
// message sends) to finish on a still-valid token before the real expiry.
// The timeline is read from the env file (seconds); each value falls back to
// its constant in SESSION_TIMELINE_FALLBACK when the variable is missing or
// invalid.
const SESSION_TIMELINE_ENV = {
  DECISION_LEAD_MS: "REACT_APP_SESSION_DECISION_LEAD_SECONDS",
  LOGOUT_BUFFER_MS: "REACT_APP_SESSION_LOGOUT_BUFFER_SECONDS",
  IDLE_THRESHOLD_MS: "REACT_APP_SESSION_IDLE_THRESHOLD_SECONDS",
};

// Used only when the env value is missing or invalid. For the API's 1h token:
// decision at 55 min after sign-in, logout at 56 min.
const SESSION_TIMELINE_FALLBACK = {
  // 5 min before expiry: renew an active user silently, or show the popup
  DECISION_LEAD_MS: 5 * 60 * 1000,
  // 4 min before expiry: forced logout when the 1 minute countdown ends
  LOGOUT_BUFFER_MS: 4 * 60 * 1000,
  // 15 min: no activity for this long at the decision point shows the popup
  IDLE_THRESHOLD_MS: 15 * 60 * 1000,
};

// Internal safety limits on renewal. Fixed in code, not read from the env:
// they guard against bugs and hangs, and a wrong value would break renewal
// for every user.
const SESSION_RENEWAL_LIMITS = {
  // A renewed token must leave at least this long before its own decision
  // point, so a token that does not extend the session (same token handed
  // back, very short lifetime, client clock far ahead) cannot start a tight
  // renewal loop.
  MIN_RENEWAL_INTERVAL_MS: 60 * 1000,
  // How long one renewer (API refresh or parent window) may take before it
  // counts as failed. Keeps a hung renewal from eating the popup's countdown:
  // with two renewers the popup still has most of its minute left.
  RENEWAL_TIMEOUT_MS: 10 * 1000,
};

const readTimelineFromEnv = (env) =>
  Object.fromEntries(
    Object.entries(SESSION_TIMELINE_ENV).map(([key, envName]) => [
      key,
      readEnvSecondsAsMs(env, envName, SESSION_TIMELINE_FALLBACK[key]),
    ])
  );

// The timeline from the env, falling back to the constants. The countdown
// needs the logout to come after the decision, so a buffer that is not
// shorter than the lead drops both env values. _timelineInvalidEnv is true
// when that happened, so tests and diagnostics can see a misconfigured env
// without having to spy on console.warn.
export const resolveSessionTiming = (env) => {
  const timeline = readTimelineFromEnv(env);
  if (timeline.LOGOUT_BUFFER_MS >= timeline.DECISION_LEAD_MS) {
    console.warn("Session timing env: the logout buffer must be shorter than the decision lead; using the fallbacks");
    return {
      ...timeline,
      DECISION_LEAD_MS: SESSION_TIMELINE_FALLBACK.DECISION_LEAD_MS,
      LOGOUT_BUFFER_MS: SESSION_TIMELINE_FALLBACK.LOGOUT_BUFFER_MS,
      _timelineInvalidEnv: true,
    };
  }
  return { ...timeline, _timelineInvalidEnv: false };
};

export const SESSION_TIMING = {
  ...resolveSessionTiming(process.env),
  ...SESSION_RENEWAL_LIMITS,
};

export const ONE_SECOND_MS = 1000;

// How the chat was signed in, set by the auth guard. Decides how the access
// token can be renewed.
export const AUTH_FLOWS = {
  // /:token chat link (AuthGuard): login API, refresh token
  CHAT_LINK: "chatLink",
  // Opened from the eventscribe website (EventScribeAuthGuard): parent window
  WEBSITE: "website",
};

// Countdown seconds at which the popup's live region speaks up, so screen
// reader users hear the time running out without an announcement per second.
export const COUNTDOWN_ANNOUNCE_AT_SECONDS = [30, 10];

// Why the warning popup is showing.
export const SESSION_WARNING_REASONS = {
  IDLE: "idle",
  RENEWAL_FAILED: "renewalFailed",
};

export const SESSION_MESSAGES = {
  WARNING_TITLE: "Your session is about to expire",
  RENEWAL_FAILED_TITLE: "We couldn't keep your session active",
  WARNING_BODY: "For your security, you will be logged out in",
  WARNING_QUESTION: "Do you want to continue your session?",
  CONTINUE_BUTTON: "Continue session",
  RENEWING_BUTTON: "Continuing...",
  CANCEL_BUTTON: "Cancel",
  COUNTDOWN_ANNOUNCEMENT: (seconds) => `About ${seconds} seconds left before you are logged out.`,
  EXPIRED: "Your session has expired. Please refresh to continue.",
};
